/**
 * Employee Incentive Report — prototype with dummy data.
 * Shows per-employee monthly incentive payable with:
 *  - Original vs Actual achieved (post credit-note / non-service adjustments)
 *  - Payment received % gate
 *  - Bracket lookup
 *  - Provisional (editable, T ≤ +2 months) → Locked transition
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import SlabEditorDialog from './SlabEditorDialog';
import { BooksCommission } from './BooksCommission';
import {
  ArrowLeft, Award, Info, CheckCircle2, XCircle, Lock, Unlock,
  Download, Edit3, TrendingUp, AlertTriangle, Wallet, Calculator, RefreshCw,
  ShieldCheck, Banknote, History, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCur = (v) => {
  if (v == null || isNaN(Number(v))) return '-';
  return Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fmtPct = (v) => v == null || isNaN(Number(v)) ? '-' : `${Number(v).toFixed(1)}%`;
const fmtMonth = (m) => {
  if (!m) return '';
  try {
    const [y, mm] = m.split('-');
    const d = new Date(Number(y), Number(mm) - 1, 1);
    return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  } catch { return m; }
};

export default function IncentiveReportPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedEmp, setSelectedEmp] = useState('all');
  const [config, setConfig] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [adjustModal, setAdjustModal] = useState({ open: false, row: null });
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [slabEditorOpen, setSlabEditorOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);
      const r = await api.get(`/reports/incentive/summary?${params}`);
      setRows(r.data.rows || []);
      setMonths(r.data.months_available || []);
      setSummary(r.data.summary || {});
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [selectedMonth]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/reports/incentive/config');
      setConfig(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadConfig(); loadSyncStatus(); }, [loadConfig]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const r = await api.get('/reports/incentive/sync-status');
      setSyncStatus(r.data);
    } catch { /* ignore */ }
  }, []);

  const onResync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/reports/incentive/resync');
      if (r.data.ok) {
        toast.success(`Synced: ${r.data.inserted} new · ${r.data.updated} updated${r.data.skipped ? ` · ${r.data.skipped} skipped` : ''}`);
      } else {
        toast.error(r.data.error || 'Sync returned no data');
      }
      load();
      loadSyncStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const onSeed = async () => {
    try {
      const r = await api.post('/reports/incentive/seed');
      if (r.data.skipped) toast.info('Dummy data already present');
      else toast.success(`Seeded ${r.data.inserted} rows across ${r.data.months.length} months`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed (admin only)'); }
  };

  const filteredRows = rows.filter(r => selectedEmp === 'all' || r.emp_code === selectedEmp);
  const employees = Array.from(new Map(rows.map(r => [r.emp_code, r.emp_name])).entries());

  const exportCsv = () => {
    if (!filteredRows.length) return;
    const cols = ['Emp Code', 'Emp Name', 'Month', 'Expected Target', 'Original Achieved',
                  'Credit Note', 'Non-Service Adj', 'Actual Achieved', 'Achievement %',
                  'Payment Received', 'Payment %', 'Bracket', 'Bracket ₹',
                  'Zoho Commission', 'Incentive Portion', 'Commission Portion',
                  'Achv Gate', 'Pay Gate', 'Final Incentive', 'Status'];
    const lines = [cols.map(c => `"${c}"`).join(',')];
    filteredRows.forEach(r => {
      const row = [
        r.emp_code, r.emp_name, r.month, r.expected_target, r.original_achieved,
        r.credit_note_adjustment, r.non_service_adjustment, r.actual_achieved, r.achievement_pct,
        r.payment_received, r.payment_received_pct, r.bracket_label, r.bracket_incentive,
        r.zoho_commission, r.incentive_portion, r.commission_portion,
        r.achievement_gate_pass ? 'PASS' : 'FAIL',
        r.payment_gate_pass ? 'PASS' : 'FAIL',
        r.final_incentive, r.status,
      ];
      lines.push(row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incentive_report_${selectedMonth || 'all'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav('/reports')} data-testid="inc-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Reports
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onResync} disabled={syncing} data-testid="inc-resync-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Resync from Zoho form'}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="inc-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Employee Incentive Report <span className="text-sm font-semibold text-slate-400">· till June 2026</span></h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Source: <span className="font-semibold text-slate-700">{syncStatus?.source_form_name || 'Retail Sales Achieved Monthly Report'}</span> · Adjustable window: T + 2 months
              {' · '}<button onClick={() => nav('/reports/fp-incentive')} className="text-blue-700 font-semibold hover:underline" data-testid="old-incentive-new-report-link">July 2026 onwards → Sales Incentive Report (Fully Paid)</button>
            </p>
          </div>
        </div>
        {syncStatus?.last_sync ? (
          <p className="text-[11px] text-slate-500 mb-5 ml-14">
            Last synced {new Date(syncStatus.last_sync.ran_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            {' · '}{syncStatus.last_sync.inserted} added · {syncStatus.last_sync.updated} refreshed
            {syncStatus.last_sync.skipped ? ` · ${syncStatus.last_sync.skipped} skipped` : ''}
          </p>
        ) : (
          <p className="text-[11px] text-slate-500 mb-5 ml-14">
            Data will be pulled from the source form on next sync. <span className="text-indigo-600">Auto-sync (if enabled in Sync Settings)</span> will keep it refreshed automatically.
          </p>
        )}

        {/* Process explainer */}
        {config && (
          <Card className="p-4 mb-5 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-violet-50/50">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                <Info className="w-4 h-4 text-indigo-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <h3 className="text-sm font-bold text-indigo-900">How the incentive is calculated</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] border-indigo-200 bg-white/70 text-indigo-800 hover:bg-white"
                    onClick={() => setSlabEditorOpen(true)}
                    data-testid="open-slab-editor"
                  >
                    <Edit3 className="w-3 h-3 mr-1" /> Edit slabs
                  </Button>
                </div>
                <ul className="text-[12px] text-indigo-900/80 space-y-1 leading-relaxed list-disc pl-4">
                  {config.rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {config.brackets.map(b => (
                    <Badge key={b.label} variant="outline" className="text-[10px] bg-white/80 text-indigo-800 border-indigo-200">
                      {b.label} → ₹{b.incentive.toLocaleString('en-IN')}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Kpi label="Records" value={summary.total_rows ?? '—'} icon={Calculator} color="slate" />
          <Kpi label="Total Payable" value={fmtCur(summary.total_incentive_payable)} icon={Wallet} color="emerald" />
          <Kpi label="Provisional" value={summary.provisional ?? '—'} icon={Unlock} color="amber" />
          <Kpi label="Finalized" value={summary.finalized ?? '—'} icon={ShieldCheck} color="blue" />
          <Kpi label="Paid" value={summary.paid ?? '—'} icon={Banknote} color="emerald" />
        </div>

        {/* Filters */}
        <Card className="p-3 mb-4 border-slate-200 bg-white">
          <div className="flex flex-wrap gap-2 items-center">
            <Label className="text-xs text-slate-600 mr-1">Month</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="inc-month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {months.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Label className="text-xs text-slate-600 ml-3 mr-1">Employee</Label>
            <Select value={selectedEmp} onValueChange={setSelectedEmp}>
              <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="inc-emp-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name} · {code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 ml-auto">Click any row for detailed breakdown</span>
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
        ) : filteredRows.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Award className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">No incentive records yet</p>
            <p className="text-xs text-slate-500 mt-1">Click "Resync from Zoho form" to pull the latest approved submissions from <b>Retail Sales Achieved Monthly Report</b>.</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 mt-4" onClick={onResync} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Resync now'}
            </Button>
          </Card>
        ) : (
          <Card className="overflow-hidden border-slate-200 shadow-sm bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2.5 px-3 font-semibold">Employee</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Month</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Expected</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Original</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-rose-600">CN</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-rose-600">Non-Service</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Actual</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Achv %</th>
                    <th className="text-right py-2.5 px-3 font-semibold" title="Achieved Payment Received with Tax % (from form)">Achv Pay Rec %</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Bracket</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Zoho Comm</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-emerald-700">Payable</th>
                    <th className="text-center py-2.5 px-3 font-semibold">Status</th>
                    <th className="py-2.5 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(r => (
                    <tr
                      key={`${r.emp_code}_${r.month}`}
                      className="border-b border-slate-100 hover:bg-amber-50/40 transition-colors cursor-pointer"
                      onClick={() => setDetailRow(r)}
                      data-testid={`inc-row-${r.emp_code}_${r.month}`}
                    >
                      <td className="py-2.5 px-3">
                        <div className="font-medium text-slate-900">{r.emp_name}</div>
                        <div className="text-[10px] text-slate-400">{r.emp_code}</div>
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 font-medium">{fmtMonth(r.month)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-600">{fmtCur(r.expected_target)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">{fmtCur(r.original_achieved)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-rose-600">{r.credit_note_adjustment > 0 ? `− ${fmtCur(r.credit_note_adjustment)}` : '—'}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-rose-600">{r.non_service_adjustment > 0 ? `− ${fmtCur(r.non_service_adjustment)}` : '—'}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-bold text-slate-900">{fmtCur(r.actual_achieved)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className={r.achievement_gate_pass ? 'text-emerald-700 font-semibold' : 'text-rose-600 font-semibold'}>
                          {fmtPct(r.achievement_pct)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className={r.payment_gate_pass ? 'text-emerald-700 font-semibold' : 'text-rose-600 font-semibold'}>
                          {fmtPct(r.payment_received_pct)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-[11px] text-slate-600">{r.bracket_label.replace('— no incentive', '')}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-600">{r.zoho_commission > 0 ? fmtCur(r.zoho_commission) : <span className="text-slate-300">—</span>}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-bold">
                        <span className={r.final_incentive > 0 ? 'text-emerald-700' : 'text-slate-400'}>{fmtCur(r.final_incentive)}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {r.status === 'paid' ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"><Banknote className="w-2.5 h-2.5 mr-1" />Paid</Badge>
                        ) : r.status === 'finalized' ? (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200"><ShieldCheck className="w-2.5 h-2.5 mr-1" />Finalized</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><Unlock className="w-2.5 h-2.5 mr-1" />Provisional</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); setAdjustModal({ open: true, row: r }); }}
                          title={r.editable ? 'Adjust' : 'View (read-only)'}
                          data-testid={`inc-adjust-${r.emp_code}_${r.month}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <DialogContent className="max-w-2xl">
          {detailRow && (
            <DetailBreakdown
              row={detailRow}
              onAdjust={() => { setAdjustModal({ open: true, row: detailRow }); setDetailRow(null); }}
              onTransition={async (to) => {
                const note = to === 'paid'
                  ? window.prompt('Payment reference / note (optional):', '') || undefined
                  : to === 'finalized'
                    ? window.prompt('Finalization note (optional):', '') || undefined
                    : window.prompt('Reason for reverting (optional):', '') || undefined;
                try {
                  const r = await api.post(`/reports/incentive/${detailRow.emp_code}/${detailRow.month}/transition`, { to_status: to, note });
                  setDetailRow(r.data);
                  toast.success(`Marked ${to}`);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || 'Transition failed'); }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust modal */}
      <AdjustModal
        open={adjustModal.open}
        row={adjustModal.row}
        onClose={() => setAdjustModal({ open: false, row: null })}
        onSaved={() => { setAdjustModal({ open: false, row: null }); load(); }}
      />
      <SlabEditorDialog
        open={slabEditorOpen}
        onClose={() => setSlabEditorOpen(false)}
        initialConfig={config}
        onSaved={() => { loadConfig(); load(); }}
      />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  const colors = {
    slate: 'from-slate-50 to-slate-100 text-slate-700 ring-slate-200',
    emerald: 'from-emerald-50 to-green-50 text-emerald-700 ring-emerald-100',
    amber: 'from-amber-50 to-orange-50 text-amber-700 ring-amber-100',
    blue: 'from-blue-50 to-sky-50 text-blue-700 ring-blue-100',
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${colors[color]} ring-1 border-0 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-7 h-7 opacity-40" />
      </div>
    </Card>
  );
}

function Gate({ pass, label, pct, required }) {
  return (
    <div className={`flex items-center justify-between p-2.5 rounded-lg border ${pass ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'}`}>
      <div className="flex items-center gap-2">
        {pass ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-600" />}
        <div>
          <div className="text-xs font-semibold text-slate-900">{label}</div>
          <div className="text-[11px] text-slate-500">Required: ≥ {required}%</div>
        </div>
      </div>
      <div className={`text-sm font-bold tabular-nums ${pass ? 'text-emerald-700' : 'text-rose-600'}`}>{pct?.toFixed(1)}%</div>
    </div>
  );
}

function DetailBreakdown({ row, onAdjust, onTransition }) {
  const statusMeta = {
    provisional: { label: 'Provisional', tone: 'amber', Icon: Unlock },
    finalized: { label: 'Finalized', tone: 'blue', Icon: ShieldCheck },
    paid: { label: 'Paid', tone: 'emerald', Icon: Banknote },
  }[row.status] || { label: row.status, tone: 'slate', Icon: Info };
  const StatusIcon = statusMeta.Icon;
  const timeline = row.timeline || [];

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-600" />
          {row.emp_name} · {fmtMonth(row.month)}
          <Badge variant="outline" className={`ml-auto text-[10px] bg-${statusMeta.tone}-50 text-${statusMeta.tone}-700 border-${statusMeta.tone}-200`}>
            <StatusIcon className="w-2.5 h-2.5 mr-1" /> {statusMeta.label}
          </Badge>
        </DialogTitle>
      </DialogHeader>
      <Tabs defaultValue="breakdown" className="mt-2">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
          <TabsTrigger value="flow">Calculation Flow</TabsTrigger>
          <TabsTrigger value="timeline">
            Timeline{timeline.length > 0 && <span className="ml-1 text-[9px] bg-slate-200 text-slate-700 rounded-full px-1.5">{timeline.length}</span>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="breakdown" className="space-y-3 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <InfoRow label="Expected Target" value={fmtCur(row.expected_target)} />
            <InfoRow label="Original Achieved" value={fmtCur(row.original_achieved)} />
            <InfoRow label="Credit Note" value={row.credit_note_adjustment > 0 ? `− ${fmtCur(row.credit_note_adjustment)}` : '—'} tone="rose" />
            <InfoRow label="Non-service Adj" value={row.non_service_adjustment > 0 ? `− ${fmtCur(row.non_service_adjustment)}` : '—'} tone="rose" />
            <InfoRow label="Actual Achieved" value={fmtCur(row.actual_achieved)} bold />
            <InfoRow label="Payment Received" value={fmtCur(row.payment_received)} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Gate pass={row.achievement_gate_pass} label="Achievement Gate" pct={row.achievement_pct} required={65} />
            <Gate pass={row.payment_gate_pass} label="Achv Payment Rec % Gate" pct={row.payment_received_pct} required={60} />
          </div>

          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Bracket match</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5">{row.bracket_label}</div>
            <div className="text-xs text-slate-500 mt-0.5">Base incentive: <span className="font-semibold text-slate-700">{fmtCur(row.bracket_incentive)}</span></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-indigo-50/40 border border-indigo-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-600">Incentive portion (bracket × gates)</div>
              <div className="text-base font-bold text-indigo-800 tabular-nums mt-0.5">{fmtCur(row.incentive_portion)}</div>
            </div>
            <div className="p-3 rounded-lg bg-violet-50/40 border border-violet-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600 flex items-center justify-between">
                <span>Zoho commission (× gates)</span>
                <span className="text-slate-400 normal-case">base {fmtCur(row.zoho_commission)}</span>
              </div>
              <div className="text-base font-bold text-violet-800 tabular-nums mt-0.5">{fmtCur(row.commission_portion)}</div>
            </div>
          </div>

          <div className={`p-4 rounded-xl text-center ${row.final_incentive > 0 ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-slate-100 border-2 border-slate-200'}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Final Incentive Payable</div>
            <div className={`text-3xl font-bold mt-1 ${row.final_incentive > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
              {fmtCur(row.final_incentive)}
            </div>
            <p className="text-[11px] text-slate-600 mt-2 leading-relaxed px-4">{row.calculation_notes}</p>
          </div>
        </TabsContent>
        <TabsContent value="flow" className="mt-3">
          <ol className="space-y-2 text-sm">
            <FlowStep n={1} title="Source data (from Retail Sales Achieved Monthly Report)">
              Expected Target <b>{fmtCur(row.expected_target)}</b>, Original Achieved <b>{fmtCur(row.original_achieved)}</b>, Payment Received <b>{fmtCur(row.payment_received)}</b>.
            </FlowStep>
            <FlowStep n={2} title="Deductions (T+2 window)">
              {(row.credit_note_adjustment > 0 || row.non_service_adjustment > 0) ? (
                <>
                  {row.credit_note_adjustment > 0 && <>Credit Note <b className="text-rose-600">−{fmtCur(row.credit_note_adjustment)}</b>. </>}
                  {row.non_service_adjustment > 0 && <>Non-service <b className="text-rose-600">−{fmtCur(row.non_service_adjustment)}</b>. </>}
                  Actual Achieved = <b>{fmtCur(row.actual_achieved)}</b>.
                </>
              ) : <>No deductions → Actual = Original = <b>{fmtCur(row.actual_achieved)}</b>.</>}
            </FlowStep>
            <FlowStep n={3} title="Bracket lookup on Actual Achieved">
              <b>{row.bracket_label}</b> → base incentive <b>{fmtCur(row.bracket_incentive)}</b>.
            </FlowStep>
            <FlowStep n={4} title="Gate checks + Zoho commission">
              Achievement {row.achievement_gate_pass ? '✓' : '✗'} ({fmtPct(row.achievement_pct)}) · Payment {row.payment_gate_pass ? '✓' : '✗'} ({fmtPct(row.payment_received_pct)})
              <div className="mt-1">Zoho commission (manual): <b>{fmtCur(row.zoho_commission)}</b> · after gates → <b>{fmtCur(row.commission_portion)}</b>.</div>
            </FlowStep>
            <FlowStep n={5} title="Final payout" last>
              Incentive <b>{fmtCur(row.incentive_portion)}</b> + Commission <b>{fmtCur(row.commission_portion)}</b> = <b className={row.final_incentive > 0 ? 'text-emerald-700' : 'text-slate-400'}>{fmtCur(row.final_incentive)}</b>
              <div className="mt-1">{row.calculation_notes}</div>
            </FlowStep>
          </ol>
        </TabsContent>
        <TabsContent value="timeline" className="mt-3">
          {timeline.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">
              <History className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No history yet. Edits and status changes will appear here.
            </div>
          ) : (
            <ol className="space-y-2.5">
              {timeline.slice().reverse().map((ev, i) => {
                const isStatus = ev.action && ev.action.includes('→');
                return (
                  <li key={i} className="flex gap-3">
                    <div className="flex flex-col items-center shrink-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] ${isStatus ? 'bg-blue-500' : 'bg-slate-400'}`}>
                        {isStatus ? <ShieldCheck className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
                      </div>
                      {i < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="pb-3 flex-1 text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{ev.actor}</span>
                        {ev.actor_role && <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 uppercase">{ev.actor_role}</Badge>}
                        <span className="text-slate-500">{isStatus ? ev.action : ev.action || 'edited'}</span>
                      </div>
                      <div className="text-slate-600 mt-0.5">{ev.details}</div>
                      {ev.note && <div className="italic text-slate-500 mt-0.5">"{ev.note}"</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(ev.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </TabsContent>
      </Tabs>
      <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
        {row.status === 'provisional' && (
          <>
            <Button size="sm" variant="outline" onClick={onAdjust}>
              <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Adjust
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onTransition('finalized')} data-testid="inc-finalize-btn">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Finalize
            </Button>
          </>
        )}
        {row.status === 'finalized' && (
          <>
            <Button size="sm" variant="outline" onClick={() => onTransition('provisional')}>
              <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Revert to provisional
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onTransition('paid')} data-testid="inc-markpaid-btn">
              <Banknote className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
            </Button>
          </>
        )}
        {row.status === 'paid' && (
          <div className="text-[11px] text-slate-500 italic w-full text-center">
            Paid · {row.paid_by ? `by ${row.paid_by}` : ''}{row.paid_at ? ` on ${new Date(row.paid_at).toLocaleDateString('en-IN')}` : ''}
          </div>
        )}
      </DialogFooter>
    </>
  );
}

function InfoRow({ label, value, bold, tone }) {
  const toneCls = tone === 'rose' ? 'text-rose-600' : tone === 'amber' ? 'text-amber-700' : tone === 'slate' ? 'text-slate-600' : 'text-slate-800';
  return (
    <div className="p-2.5 rounded-lg border border-slate-200 bg-white">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${toneCls} mt-0.5 tabular-nums`}>{value}</div>
    </div>
  );
}

function FlowStep({ n, title, children, last }) {
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">{n}</div>
        {!last && <div className="w-px flex-1 bg-slate-200 mt-1" />}
      </div>
      <div className="pb-3 flex-1">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{children}</div>
      </div>
    </li>
  );
}

function AdjustModal({ open, row, onClose, onSaved }) {
  const [cn, setCn] = useState('');
  const [ns, setNs] = useState('');
  const [zc, setZc] = useState('');
  const [zcTouched, setZcTouched] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (row) {
      setCn(String(row.credit_note_adjustment || 0));
      setNs(String(row.non_service_adjustment || 0));
      setZc(String(row.zoho_commission || 0));
      setZcTouched(false);
      setNote('');
    }
  }, [row]);
  if (!row) return null;

  const save = async () => {
    try {
      const v = Number(cn);
      const nsv = Number(ns);
      const zcv = Number(zc);
      if (isNaN(v) || v < 0) { toast.error('Enter a valid non-negative Credit Note value'); return; }
      if (isNaN(nsv) || nsv < 0) { toast.error('Enter a valid non-negative Non-service adjustment'); return; }
      if (isNaN(zcv) || zcv < 0) { toast.error('Enter a valid non-negative Zoho commission'); return; }
      if (v + nsv > row.original_achieved) { toast.error('Total deductions cannot exceed Original Achieved'); return; }
      await api.post(`/reports/incentive/${row.emp_code}/${row.month}/adjust`, {
        credit_note_adjustment: v,
        non_service_adjustment: nsv,
        zoho_commission: zcv,
        note: note || undefined,
      });
      toast.success('Adjustment saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const preview = Math.max(0, (row.original_achieved || 0) - Number(cn || 0) - Number(ns || 0));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Adjust incentive inputs
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-1">
            <div><span className="text-slate-500">Employee:</span> <b className="text-slate-800">{row.emp_name} · {row.emp_code}</b></div>
            <div><span className="text-slate-500">Month:</span> <b className="text-slate-800">{fmtMonth(row.month)}</b></div>
            <div><span className="text-slate-500">Original Achieved:</span> <b className="text-slate-800">{fmtCur(row.original_achieved)}</b></div>
            <BooksCommission row={row} onLoaded={b => { if (!zcTouched) setZc(String(b.total_commission)); }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Credit Note (₹)</Label>
              <Input type="number" min="0" value={cn} onChange={e => setCn(e.target.value)} data-testid="inc-adjust-cn-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Post-billing credit notes issued</p>
            </div>
            <div>
              <Label className="text-xs">Non-service Adjustment (₹)</Label>
              <Input type="number" min="0" value={ns} onChange={e => setNs(e.target.value)} data-testid="inc-adjust-ns-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Centre service not provided</p>
            </div>
          </div>
          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Preview · Actual Achieved</div>
            <div className="text-lg font-bold text-amber-900 tabular-nums mt-0.5">{fmtCur(preview)}</div>
            <div className="text-[10px] text-amber-700/70 mt-0.5">
              = Original {fmtCur(row.original_achieved)} − CN {fmtCur(Number(cn || 0))} − Non-service {fmtCur(Number(ns || 0))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Zoho Books commission (₹) — manual entry</Label>
            <Input type="number" min="0" value={zc} onChange={e => { setZc(e.target.value); setZcTouched(true); }} placeholder="0" data-testid="inc-adjust-zc-input" />
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
              Added to bracket incentive. If one gate fails → 50%. If both fail → only 50% of this commission is paid (no bracket).
            </p>
          </div>
          <div>
            <Label className="text-xs">Reason / note (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g., Centre service not provided for 2 students" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" onClick={save} data-testid="inc-adjust-save">Save adjustment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
